/**
 * -----------------------------------------------------------------------------
 * Account Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the account details.
 */

import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MaintenanceFlag } from "../../../api/types/Sysadmin";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDraggableToggleList,
  AdminFormFieldDropdown,
  AdminFormFieldDurationInput,
  AdminFormFieldInput,
  AdminFormFieldMultiCheckbox,
  TSortableToggleItem,
} from "../../../components/admin/formFields";
import TickCheckbox from "../../../components/tickCheckbox";
import {
  ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE,
  ACCOUNT_MAP_ICON_SIZE_OPTIONS,
  APP_CONFIG,
  INFOBOX_DISPLAY_OPTIONS,
  TIMEZONE_OPTIONS,
} from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";
import {
  DEFAULT_OVERLAY_OPTIONS,
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS_OPTIONS,
  ENABLE_MAP_CLUSTERING_OPTIONS,
  LATITUDE_LONGITUDE_FORMAT_OPTIONS,
  PRESSURE_UNITS_OPTIONS,
  ROUTE_LINE_THICKNESS_OPTIONS,
  SCORECARD_WEIGHT_FACTOR_OPTIONS,
  SPEED_UNITS_OPTIONS,
  TEMPRATURE_UNITS_OPTIONS,
  VOLUME_UNITS_OPTIONS,
} from "./constants";
import mapPreview from '../../../assets/images/map_preview.png';
import assetLabel from '../../../assets/images/asset_label.png';
import { MaintenanceInputForm } from "../maintenance/components/maintenanceInputForm";
import svgIcons from "../../../assets/svg/svgIcons";
import { MaintenancePerodicInputForm } from "../maintenance/components/maintenancePerodicForm";


interface AccountGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => any;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  formikValidateForm?: () => Promise<any>;
  userCanEdit: boolean;
  loadingData: boolean;
  maintenanceFlag?: MaintenanceFlag;
  setModifiedCheckBoxData?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

const mirroredAssetToggleFields = [
  "deviceid",
  "description",
  "displayname",
  "drivername",
];

const syncMirroredAssetToggles = (
  sourceValues: string[],
  targetValues: string[],
  targetAllowedValues: string[]
) => {
  const sourceSet = new Set(sourceValues);
  const targetSet = new Set(
    targetValues.filter((value) => targetAllowedValues.includes(value))
  );

  mirroredAssetToggleFields.forEach((field) => {
    if (!targetAllowedValues.includes(field)) return;

    if (sourceSet.has(field)) {
      targetSet.add(field);
    } else {
      targetSet.delete(field);
    }
  });

  return Array.from(targetSet);
};

export const AccountGeneralDetailForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("account_id")}
        type="text"
        id="account_id"
        name="account_id"
        value={values.account_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.account_id}
        touched={touched.account_id}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
      />
      <AdminFormFieldInput
        label={t("account_description")}
        type="text"
        id="account_description"
        name="account_description"
        value={values.account_description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.account_description}
        touched={touched.account_description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        label={t("contact_name")}
        type="text"
        id="contact_name"
        name="contact_name"
        value={values.contact_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_name}
        touched={touched.contact_name}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldInput
        label={t("contact_phone_number")}
        type="text"
        id="contact_phone_number"
        name="contact_phone_number"
        value={values.contact_phone_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_phone_number}
        touched={touched.contact_phone_number}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldInput
        label={t("contact_email")}
        type="email"
        id="contact_email"
        name="contact_email"
        value={values.contact_email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_email}
        touched={touched.contact_email}
        disabled={!userCanEdit}
        customWrapperClass="col-span-4"
      />
      <div className="col-span-12 grid grid-cols-12">
        <AdminFormFieldCheckbox
          label={t("use_2fa")}
          id="use_2fa"
          type="checkbox"
          name="use_2fa"
          checked={values.use_2fa}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.use_two_factor}
          error={errors.use_two_factor}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-4"
        />
        {/* <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("enable_map_clustering")}
          id="enable_map_clustering"
          name="enable_map_clustering"
          value={`${values.enable_map_clustering}`}
          options={ENABLE_MAP_CLUSTERING_OPTIONS}
          onChange={(e) => {
            formikSetValue("enable_map_clustering", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("enable_map_clustering", true);
            handleBlur(e);
          }}
          touched={touched.enable_map_clustering}
          error={errors.enable_map_clustering}
          disabled={!userCanEdit}
          detailsFormField={true}
        /> */}
      </div>
    </div>
  );
};

export const AccountMapSettingsForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  const infoBoxFields = [
    {
      key: "infobox_asset_id",
      label: "ID",
      value: "475",
    },
    {
      key: "infobox_asset_desc",
      label: "Desc",
      value: "475",
    },
    {
      key: "infobox_asset_display",
      label: "Display",
      value: "475",
    },
    {
      key: "infobox_driver",
      label: "Driver",
      value: "475",
    },
  ] as const;

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldMultiCheckbox
        label={t("map_infobox_information")}
        id="infobox_display_fields"
        options={INFOBOX_DISPLAY_OPTIONS}
        values={{
          infobox_asset_desc: values.infobox_asset_desc,
          infobox_asset_display: values.infobox_asset_display,
          infobox_driver: values.infobox_driver,
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={!userCanEdit}
        detailsFormField={true}
        error={errors.infobox_asset_id}
        customWrapperClass="col-span-6"
      />
      <div className="col-span-6 col-start-1 rounded-lg bg-sky-100 p-3">
        <p className="mb-2 text-sm font-semibold">
         {t("preview_asset_map")}
        </p>

        <div className="relative h-64 overflow-hidden rounded-md">
          <img
            src={mapPreview}
            alt="Asset map preview"
            className="absolute inset-0 h-full w-full object-cover object-[center_20%]"
          />
          <div className="absolute left-1/2 top-1/2 w-40 -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-3 shadow-lg">
            {infoBoxFields
              .filter((field) => values[field.key])
              .map((field, index) => (
                <div
                  key={field.key}
                  className={`py-1 text-center ${index !== infoBoxFields.length - 1 ? "border-b border-gray-100" : ""}
                    ${field.key === "infobox_asset_id" ? "text-sm font-semibold text-accent-blue-dark" : "text-xs"}`}
                >
                  <span className="">{field.label}:</span>{" "}
                  {field.value}
                </div>
              ))}
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-x-[8px] border-t-[8px] border-x-transparent border-t-white" />
          </div>

        </div>
      </div>
      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label={t("infobox_always_open")}
          id="infobox_always_open"
          type="checkbox"
          name="infobox_always_open"
          checked={values.infobox_always_open}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.infobox_always_open}
          error={errors.infobox_always_open}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
    </div>
  )
}

export const AccountReportingOperationalForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
    return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("minidleseconds")}
        type="number"
        id="minidleseconds"
        name="minidleseconds"
        value={values.minidleseconds}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.minidleseconds}
        error={errors.minidleseconds}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      <AdminFormFieldDurationInput
        label={t("cameraliveviewduration")}
        id="cameraliveviewduration"
        name="cameraliveviewduration"
        value={values.cameraliveviewduration}
        loading={loadingData}
        onChange={(totalSeconds) => {
          formikSetValue("cameraliveviewduration", totalSeconds);
        }}
        onBlur={() => {
          formikSetTouched("cameraliveviewduration", true);
        }}
        touched={touched.cameraliveviewduration}
        error={errors.cameraliveviewduration}
        disabled={!userCanEdit}
        detailsFormField={true}
        minutesAsInput={true}
        showSeconds={true}
        hideHours={true}
        maxMinutes={10}
      />
    </div>
  )
}

export const AccountUnitPreferenceForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
    return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("timezone")}
        id="timezone"
        name="timezone"
        value={values.timezone}
        options={TIMEZONE_OPTIONS}
        onChange={(e) => {
          formikSetValue("timezone", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("timezone", true);
          handleBlur(e);
        }}
        touched={touched.timezone}
        error={errors.timezone}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("speed_units")}
        id="speed_units"
        name="speed_units"
        value={values.speed_units}
        options={SPEED_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("speed_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("speed_units", true);
          handleBlur(e);
        }}
        touched={touched.speed_units}
        error={errors.speed_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("distance_units")}
        id="distance_units"
        name="distance_units"
        value={values.distance_units}
        options={DISTANCE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("distance_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("distance_units", true);
          handleBlur(e);
        }}
        touched={touched.distance_units}
        error={errors.distance_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("volume_units")}
        id="volume_units"
        name="volume_units"
        value={values.volume_units}
        options={VOLUME_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("volume_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("volume_units", true);
          handleBlur(e);
        }}
        touched={touched.volume_units}
        error={errors.volume_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("economy_units")}
        id="economy_units"
        name="economy_units"
        value={values.economy_units}
        options={ECONOMY_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("economy_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("economy_units", true);
          handleBlur(e);
        }}
        touched={touched.economy_units}
        error={errors.economy_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("pressure_units")}
        id="pressure_units"
        name="pressure_units"
        value={values.pressure_units}
        options={PRESSURE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("pressure_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("pressure_units", true);
          handleBlur(e);
        }}
        touched={touched.pressure_units}
        error={errors.pressure_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("temperature_units")}
        id="temperature_units"
        name="temperature_units"
        value={values.temperature_units}
        options={TEMPRATURE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("temperature_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("temperature_units", true);
          handleBlur(e);
        }}
        touched={touched.temperature_units}
        error={errors.temperature_units}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

    </div>
  )
}

export const AccountFuelCostForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("private_cost")}
        type="text"
        id="private_cost"
        name="private_cost"
        value={values.private_cost}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.private_cost}
        touched={touched.private_cost}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
    </div>
  )
}

export const AccountMaintenanceIntervalLabelForm: FC<
  AccountGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  maintenanceFlag,
  setModifiedCheckBoxData,
}) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "dashboard.profile.account.form",
    });
    // State to manage checkbox states
    const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
      {}
    );

    // Handle checkbox changes
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, checked } = e.target;
      setCheckboxState((prevState) => ({
        ...prevState,
        [id]: checked,
      }));
      setModifiedCheckBoxData?.((prevState) => ({
        ...prevState,
        [id]: checked,
      }));
    };

    useEffect(() => {
      if (maintenanceFlag) {
        const checkboxData = {
          last_maintenance_1: !!maintenanceFlag?.maint_flag_km0,
          last_maintenance_2: !!maintenanceFlag?.maint_flag_km1,
          last_maintenance_3: !!maintenanceFlag?.maint_flag_km2,
          last_maintenance_4: !!maintenanceFlag?.maint_flag_km3,
          last_maintenance_5: !!maintenanceFlag?.maint_flag_km4,
          last_maintenance_6: !!maintenanceFlag?.maint_flag_km5,
          last_maintenance_7: !!maintenanceFlag?.maint_flag_km6,
          last_maintenance_8: !!maintenanceFlag?.maint_flag_km7,
          last_maintenance_9: !!maintenanceFlag?.maint_flag_km8,
          last_maintenance_10: !!maintenanceFlag?.maint_flag_km9,
          last_eng_hours_maint_1: !!maintenanceFlag?.maint_flag_hr0,
          last_eng_hours_maint_2: !!maintenanceFlag?.maint_flag_hr1,
          last_eng_hours_maint_3: !!maintenanceFlag?.maint_flag_hr2,
          last_eng_hours_maint_4: !!maintenanceFlag?.maint_flag_hr3,
          last_eng_hours_maint_5: !!maintenanceFlag?.maint_flag_hr4,
          last_service_time_1: !!maintenanceFlag?.maint_flag_ft0,
          last_service_time_2: !!maintenanceFlag?.maint_flag_ft1,
          last_service_time_3: !!maintenanceFlag?.maint_flag_ft2,
          last_service_time_4: !!maintenanceFlag?.maint_flag_ft3,
          last_service_time_5: !!maintenanceFlag?.maint_flag_ft4,
        };
        setCheckboxState(checkboxData);
      }
    }, [maintenanceFlag]);

    const maintenanceItems = [
      "last_maintenance_1",
      "last_maintenance_2",
      "last_maintenance_3",
      "last_maintenance_4",
      "last_maintenance_5",
      "last_maintenance_6",
      "last_maintenance_7",
      "last_maintenance_8",
      "last_maintenance_9",
      "last_maintenance_10"
    ];

    const engineHourItems=[
      "last_eng_hours_maint_1",
      "last_eng_hours_maint_2",
      "last_eng_hours_maint_3",
      "last_eng_hours_maint_4",
      "last_eng_hours_maint_5"
    ]

    const serviceDateItems=[
      "last_service_time_1",
      "last_service_time_2",
      "last_service_time_3",
      "last_service_time_4",
      "last_service_time_5"
    ]

    const selectedMaintenanceItems = maintenanceItems.filter(
      (id) => checkboxState[id]);
    const selectedEngineHourItems= engineHourItems.filter(
      (id) => checkboxState[id]);
    const selectedServiceDateItems= serviceDateItems.filter(
    (id) => checkboxState[id]);
    const index = 1;
    return (
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-3 gap-4">
        <h2 className="col-span-3 text-lg font-semibold text-gray-800">
          {index}. {t("last_maintenance__label")}:
        </h2>
        <span className="col-span-3 w-full pb-4 text-sm">{t("last_maintenance_text")}</span>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_1"
            wrapperClass="w-6 h-6"
            isChecked={!!checkboxState["last_maintenance_1"]}
            handleChange={handleCheckboxChange}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_1")}
            type="text"
            id="last_maintenance_1"
            name="last_maintenance_1"
            value={values.last_maintenance_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_1}
            touched={touched.last_maintenance_1}
            disabled={!(userCanEdit && checkboxState["last_maintenance_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_2"
            wrapperClass="w-6 h-6"
            isChecked={!!checkboxState["last_maintenance_2"]}
            handleChange={handleCheckboxChange}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_2")}
            type="text"
            id="last_maintenance_2"
            name="last_maintenance_2"
            value={values.last_maintenance_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_2}
            touched={touched.last_maintenance_2}
            disabled={!(userCanEdit && checkboxState["last_maintenance_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_3")}
            type="text"
            id="last_maintenance_3"
            name="last_maintenance_3"
            value={values.last_maintenance_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_3}
            touched={touched.last_maintenance_3}
            disabled={!(userCanEdit && checkboxState["last_maintenance_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_4")}
            type="text"
            id="last_maintenance_4"
            name="last_maintenance_4"
            value={values.last_maintenance_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_4}
            touched={touched.last_maintenance_4}
            disabled={!(userCanEdit && checkboxState["last_maintenance_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_5"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_5"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_5")}
            type="text"
            id="last_maintenance_5"
            name="last_maintenance_5"
            value={values.last_maintenance_5}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_5}
            touched={touched.last_maintenance_5}
            disabled={!(userCanEdit && checkboxState["last_maintenance_5"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_6"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_6"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_6")}
            type="text"
            id="last_maintenance_6"
            name="last_maintenance_6"
            value={values.last_maintenance_6}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_6}
            touched={touched.last_maintenance_6}
            disabled={!(userCanEdit && checkboxState["last_maintenance_6"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_7"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_7"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_7")}
            type="text"
            id="last_maintenance_7"
            name="last_maintenance_7"
            value={values.last_maintenance_7}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_7}
            touched={touched.last_maintenance_7}
            disabled={!(userCanEdit && checkboxState["last_maintenance_7"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_8"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_8"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_8")}
            type="text"
            id="last_maintenance_8"
            name="last_maintenance_8"
            value={values.last_maintenance_8}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_8}
            touched={touched.last_maintenance_8}
            disabled={!(userCanEdit && checkboxState["last_maintenance_8"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_9"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_9"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_9")}
            type="text"
            id="last_maintenance_9"
            name="last_maintenance_9"
            value={values.last_maintenance_9}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_9}
            touched={touched.last_maintenance_9}
            disabled={!(userCanEdit && checkboxState["last_maintenance_9"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_10"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_10"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_10")}
            type="text"
            id="last_maintenance_10"
            name="last_maintenance_10"
            value={values.last_maintenance_10}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_10}
            touched={touched.last_maintenance_10}
            disabled={!(userCanEdit && checkboxState["last_maintenance_10"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="col-span-3 rounded-lg bg-sky-100 p-3">
          <p className="mb-2 text-sm font-semibold">
            {t("preview")} {t("last_maintenance__label")}
          </p>
          <div className="flex items-center justify-between bg-blue-200 px-4 py-3">
            <span className="font-semibold text-accent-blue-dark">
              {t("last_maintenance__label")}
            </span>

            <img src={svgIcons.CollapseIcon} />
          </div>
          <div className="pt-4 px-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
            {selectedMaintenanceItems?.map((id) => (
              <MaintenanceInputForm
                key={id} // Add a unique key prop
                label={values[id] || t(id)} // Use the label from labelData
                maintenanceTypeId="maintenanceTypeId"
                maintenanceIntervalId="maintenanceIntervalId"
                values={{ maintenanceTypeId: "0", maintenanceIntervalId: "0" }}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formikSetValue}
                handleBlur={handleBlur}
                formikSetTouched={formikSetTouched}
                userCanEdit={false}
                loadingData={false}
                distanceUnit={"0"}
                bottomLabel={"in miles"}
              />
            ))}
          </div>
        </div>
        <div className="col-span-3 pt-6"></div>
        <h2 className="col-span-3 text-lg pb-2 font-semibold text-gray-800">
          {index + 1}. {t("engine_hours_maintenance__label")}:
        </h2>
        <span className="col-span-3 w-full pb-4 text-sm">{t("engine_hours_maintenance_text")}</span>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_1"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_1"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_1")}
            type="text"
            id="last_eng_hours_maint_1"
            name="last_eng_hours_maint_1"
            value={values.last_eng_hours_maint_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_1}
            touched={touched.last_eng_hours_maint_1}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_2"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_2"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_2")}
            type="text"
            id="last_eng_hours_maint_2"
            name="last_eng_hours_maint_2"
            value={values.last_eng_hours_maint_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_2}
            touched={touched.last_eng_hours_maint_2}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_3")}
            type="text"
            id="last_eng_hours_maint_3"
            name="last_eng_hours_maint_3"
            value={values.last_eng_hours_maint_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_3}
            touched={touched.last_eng_hours_maint_3}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_4")}
            type="text"
            id="last_eng_hours_maint_4"
            name="last_eng_hours_maint_4"
            value={values.last_eng_hours_maint_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_4}
            touched={touched.last_eng_hours_maint_4}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="col-span-3 grid grid-cols-3 gap-4">
          <div className="flex w-full gap-1">
            <TickCheckbox
              id="last_eng_hours_maint_5"
              wrapperClass="w-6 h-6"
              handleChange={handleCheckboxChange}
              isChecked={!!checkboxState["last_eng_hours_maint_5"]}
              disabled={!userCanEdit}
            />
            <AdminFormFieldInput
              label={t("last_eng_hours_maint_5")}
              type="text"
              id="last_eng_hours_maint_5"
              name="last_eng_hours_maint_5"
              value={values.last_eng_hours_maint_5}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.last_eng_hours_maint_5}
              touched={touched.last_eng_hours_maint_5}
              disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_5"])}
              detailsFormField={true}
              customWrapperClass="!w-full"
            />
          </div>
        </div>
          <div className="col-span-3 rounded-lg bg-sky-100 p-3">
          <p className="mb-2 text-sm font-semibold">
            {t("preview")} {t("engine_hours_maintenance__label")}
          </p>
          <div className="flex items-center justify-between bg-blue-200 px-4 py-3">
            <span className="font-semibold text-accent-blue-dark">
              {t("engine_hours_maintenance__label")}
            </span>

            <img src={svgIcons.CollapseIcon} />
          </div>
          <div className="pt-4 px-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
            {selectedEngineHourItems?.map((id) => (
              <MaintenanceInputForm
                key={id} // Add a unique key prop
                label={values[id] || t(id)} // Use the label from labelData
                maintenanceTypeId="maintenanceTypeId"
                maintenanceIntervalId="maintenanceIntervalId"
                values={{ maintenanceTypeId: "0", maintenanceIntervalId: "0" }}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formikSetValue}
                handleBlur={handleBlur}
                formikSetTouched={formikSetTouched}
                userCanEdit={false}
                loadingData={false}
                distanceUnit="0"
                bottomLabel="in hours"
              />
            ))}
          </div>
        </div>
        <div className="col-span-3 pt-6"></div>
        <h2 className="col-span-3 pb-2 text-lg font-semibold text-gray-800">
          {index + 2}. {t("last_service_time__label")}:
        </h2>
        <span className="col-span-3 w-full pb-4 text-sm">{t("last_service_time_text")}</span>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_1"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_1"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_1")}
            type="text"
            id="last_service_time_1"
            name="last_service_time_1"
            value={values.last_service_time_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_1}
            touched={touched.last_service_time_1}
            disabled={!(userCanEdit && checkboxState["last_service_time_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_2"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_2"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_2")}
            type="text"
            id="last_service_time_2"
            name="last_service_time_2"
            value={values.last_service_time_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_2}
            touched={touched.last_service_time_2}
            disabled={!(userCanEdit && checkboxState["last_service_time_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_3")}
            type="text"
            id="last_service_time_3"
            name="last_service_time_3"
            value={values.last_service_time_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_3}
            touched={touched.last_service_time_3}
            disabled={!(userCanEdit && checkboxState["last_service_time_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_4")}
            type="text"
            id="last_service_time_4"
            name="last_service_time_4"
            value={values.last_service_time_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_4}
            touched={touched.last_service_time_4}
            disabled={!(userCanEdit && checkboxState["last_service_time_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_5"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_5"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_5")}
            type="text"
            id="last_service_time_5"
            name="last_service_time_5"
            value={values.last_service_time_5}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_5}
            touched={touched.last_service_time_5}
            disabled={!(userCanEdit && checkboxState["last_service_time_5"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="col-span-3 rounded-lg bg-sky-100 p-3">
          <p className="mb-2 text-sm font-semibold">
            {t("preview")} {t("last_service_time__label")}
          </p>
          <div className="flex items-center justify-between bg-blue-200 px-4 py-3">
            <span className="font-semibold text-accent-blue-dark">
              {t("last_service_time__label")}
            </span>

            <img src={svgIcons.CollapseIcon} />
          </div>
          <div className="pt-4 px-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
            {selectedServiceDateItems?.map((id) => (
              <MaintenancePerodicInputForm
                key={id} // Add a unique key prop
                label={values[id] || t(id)} // Use the label from labelData
                maintenanceTypeId="maintenanceTypeId"
                serviceIntervalId="serviceIntervalId"
                notificationWindowId="notificationWindowId"
                values={{ maintenanceTypeId: "0", serviceIntervalId: "0", notificationWindowId: "0"  }}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                formikSetValue={formikSetValue}
                handleBlur={handleBlur}
                formikSetTouched={formikSetTouched}
                userCanEdit={false}
                loadingData={false}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

export const AccountScorecardWeightFactorsForm: FC<
  AccountGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "dashboard.profile.account.form",
    });
    return (
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
        {[
          "harsh_braking",
          "harsh_acceleration",
          "speeding",
          "reverse",
          "seatbelt_off",
          "harsh_cornering",
          "idle_ratio",
          "impact_crash_ai",
          "cellphone_use_ai",
          "distracted_driving_ai",
          "drinking_eating_ai",
          "smoking_ai",
          "possible_fatiuge_ai",
          "obstructed_camera_ai",
          "tailgating_ai",
          "scorewtfrontcollisionai",
          "scorewtpedestriancollisionai",
          "scorewtlanedepartureai",
          "scorewtstopandgoai",
          "scorewtrollingstopai",
          "scorewtcamerapowerlossai",
          "scorewtcameracoverremovalai",
          "scorewt75for30ai",
        ].map((field, i) => (
          <AdminFormFieldDropdown
            key={`field_${i}`}
            loadingData={loadingData}
            label={t(field)}
            id={field}
            name={field}
            value={values?.[field]}
            options={SCORECARD_WEIGHT_FACTOR_OPTIONS}
            onChange={(e) => {
              formikSetValue(field, e?.value);
            }}
            onBlur={(e) => {
              formikSetTouched(field, true);
              handleBlur(e);
            }}
            touched={touched?.[field]}
            error={errors?.[field]}
            disabled={!userCanEdit}
            detailsFormField={true}
            customWrapperClass="col-span-4"
          />
        ))}
      </div>
    );
  };

export const AccountAssetLabelConfigForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  formikValidateForm,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const assetLabelItems = useMemo<TSortableToggleItem[]>(() => {
    const allowedLabelComponents =
      APP_CONFIG.ASSET_CONFIGS.LABEL_COMPONENTS.filter(
        (option) => showDriver || option.value !== "drivername"
      );
    const allowedValues = allowedLabelComponents.map((option) => option.value);

    // Default only when the field is unset; preserve [] so users can uncheck all.
    const rawSelectedValues = Array.isArray(values.asset_label_fields)
      ? values.asset_label_fields
      : APP_CONFIG.ASSET_CONFIGS.DEFAULT_LABEL_CONFIG;

    const selectedValues = rawSelectedValues.filter((value: string) =>
      allowedValues.includes(value)
    );

    const labelByValue = new Map(
      allowedLabelComponents.map((option) => [option.value, option.label])
    );

    const orderedEnabled = selectedValues.map((value: string) => ({
      value,
      label: labelByValue.get(value) ?? value,
      enabled: true,
    }));

    const disabledItems = allowedLabelComponents
      .filter((option) => !selectedValues.includes(option.value))
      .map((option) => ({
        ...option,
        enabled: false,
      }));

    return [...orderedEnabled, ...disabledItems];
  }, [showDriver, values.asset_label_fields]);

  const assetSearchableItems = useMemo<TSortableToggleItem[]>(() => {
    const allowedSearchableFields =
      APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS.filter(
        (option) =>
          showDriver || !["drivername", "driverid"].includes(option.value)
      );
    const allowedValues = allowedSearchableFields.map((option) => option.value);

    const selectedValues = Array.isArray(values.asset_searchable_fields)
      ? values.asset_searchable_fields.filter((value: string) =>
        allowedValues.includes(value)
      )
      : // all values in APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS are searchable by default if not set in DB
      allowedValues;

    // Keep a stable display order for non-draggable mode.
    return allowedSearchableFields.map((option) => ({
      ...option,
      enabled: selectedValues.includes(option.value),
    }));
  }, [showDriver, values.asset_searchable_fields]);

  const ASSET_LABEL_EXAMPLE_VALUES: Record<string, string> = {
    deviceid: "1024",
    description: "Truck 1024",
    displayname: "Truck 1024",
    drivername: "John Smith",
  };

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDraggableToggleList
        loadingData={loadingData}
        label={t("asset_label_fields")}
        id="asset_label_fields"
        items={assetLabelItems}
        isDraggable={true}
        previewRenderer={(currentItems) => {
          const previewText = currentItems
            .filter((item) => item.enabled)
            .map((item) => item.label)
            .join(" | ");
          const exampleText = currentItems.filter((item) => item.enabled)
            .map((item) => ASSET_LABEL_EXAMPLE_VALUES[item.value]).join(" | ");

          return (
            <>
              <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700 break-words">
                <span className="font-semibold">Preview:</span>{" "}
                {previewText || "-"}
              </div>
              <div className="col-span-6 col-start-1 rounded-lg bg-sky-100 p-3">
                <p className="mb-2 text-sm font-semibold">
                  {t("preview_asset_label")}
                </p>

                <div className="h-64 overflow-hidden rounded-md">
                  <div className="relative inline-block h-full max-w-full">
                    <img
                      src={assetLabel}
                      alt="Asset Label preview"
                      className="block h-full max-w-full object-contain object-[center_20%]"
                    />

                    <div className="bg-gray-100 absolute left-[42%] top-[50%] w-[min(20rem,60%)] md:top-[66.67%] -translate-x-1/2 -translate-y-1/2 rounded-md p-[2%] text-[clamp(0.625rem,1.2vw,0.875rem)] font-semibold text-accent-blue-dark">
                      {exampleText || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        }}
        onChange={async (updatedItems) => {
          const nextLabelValues = updatedItems
            .filter((item) => item.enabled)
            .map((item) => item.value);

          const allowedSearchableValues =
            APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS.filter(
              (option) =>
                showDriver || !["drivername", "driverid"].includes(option.value)
            ).map((option) => option.value);

          const currentSearchableValues = Array.isArray(
            values.asset_searchable_fields
          )
            ? values.asset_searchable_fields.filter((value: string) =>
              allowedSearchableValues.includes(value)
            )
            : allowedSearchableValues;

          const nextSearchableValues = syncMirroredAssetToggles(
            nextLabelValues,
            currentSearchableValues,
            allowedSearchableValues
          );

          await Promise.all([
            formikSetValue("asset_label_fields", nextLabelValues, false),
            formikSetValue(
              "asset_searchable_fields",
              nextSearchableValues,
              false
            ),
          ]);

          await formikValidateForm?.();
        }}
        onBlur={() => {
          formikSetTouched("asset_label_fields", true, false);
        }}
        touched={touched.asset_label_fields}
        error={errors.asset_label_fields}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      <AdminFormFieldDraggableToggleList
        loadingData={loadingData}
        label={t("asset_searchable_fields")}
        id="asset_searchable_fields"
        items={assetSearchableItems}
        isDraggable={false}
        onChange={async (updatedItems) => {
          const nextSearchableValues = updatedItems
            .filter((item) => item.enabled)
            .map((item) => item.value);

          const allowedLabelValues =
            APP_CONFIG.ASSET_CONFIGS.LABEL_COMPONENTS.filter(
              (option) => showDriver || option.value !== "drivername"
            ).map((option) => option.value);

          const currentLabelValues = (
            Array.isArray(values.asset_label_fields)
              ? values.asset_label_fields
              : APP_CONFIG.ASSET_CONFIGS.DEFAULT_LABEL_CONFIG
          ).filter((value: string) => allowedLabelValues.includes(value));

          const nextLabelValues = syncMirroredAssetToggles(
            nextSearchableValues,
            currentLabelValues,
            allowedLabelValues
          );

          await Promise.all([
            formikSetValue(
              "asset_searchable_fields",
              nextSearchableValues,
              false
            ),
            formikSetValue("asset_label_fields", nextLabelValues, false),
          ]);

          await formikValidateForm?.();
        }}
        onBlur={() => {
          formikSetTouched("asset_searchable_fields", true, false);
        }}
        touched={touched.asset_searchable_fields}
        error={errors.asset_searchable_fields}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      {APP_CONFIG.DEBUG.ALL && (
        <>
          <div className="col-span-6">
            <pre>{JSON.stringify(values.asset_label_fields, null, 2)}</pre>
          </div>
          <div className="col-span-6">
            <pre>{JSON.stringify(values.asset_searchable_fields, null, 2)}</pre>
          </div>
        </>
      )}
    </div>
  );
};
